import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import {
    ShieldCheck,
    KeyRound,
    Eye,
    EyeOff,
    CheckCircle2,
    AlertTriangle,
    Lock,
    Loader2,
} from "lucide-react";

const RULES = [
    { test: (v) => v.length >= 8, label: "At least 8 characters" },
    { test: (v) => /[A-Za-z]/.test(v), label: "Includes a letter" },
    { test: (v) => /\d/.test(v), label: "Includes a number" },
];

export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const nav = useNavigate();

    const [checking, setChecking] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [tokenEmail, setTokenEmail] = useState("");
    const [tokenError, setTokenError] = useState("");

    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!token) {
            setChecking(false);
            setTokenError("No reset token provided. Request a new link.");
            return;
        }
        (async () => {
            try {
                const r = await api.post("/auth/reset-password/validate", { token });
                setTokenValid(true);
                setTokenEmail(r.data.email || "");
            } catch (err) {
                setTokenError(formatApiError(err));
            } finally {
                setChecking(false);
            }
        })();
    }, [token]);

    const rulesState = RULES.map((r) => ({ ...r, pass: r.test(pw) }));
    const strongEnough = rulesState.every((r) => r.pass);
    const matches = pw && pw === pw2;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (busy || !strongEnough || !matches) return;
        setBusy(true);
        setError("");
        try {
            await api.post("/auth/reset-password", { token, new_password: pw });
            setDone(true);
            setTimeout(() => nav("/login", { replace: true }), 2400);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-5 login-scope">
            <div className="hidden lg:flex lg:col-span-3 relative overflow-hidden text-white p-10 xl:p-14 flex-col login-hero">
                <div className="login-hero__grid" aria-hidden />
                <div className="login-hero__glow" aria-hidden />
                <div className="relative z-10 max-w-2xl mt-auto mb-auto">
                    <div className="login-brandmark" style={{ width: 60, height: 60, borderRadius: 16 }}>
                        <ShieldCheck size={28} strokeWidth={2} />
                    </div>
                    <h1 className="login-hero__title mt-6">
                        Choose a new password.
                    </h1>
                    <p className="login-hero__tagline">
                        Once set, this link is burned. Anyone with a stolen copy of it becomes useless.
                    </p>
                    <div className="login-hero__accent-line" />
                    <div className="mt-8 max-w-md text-[13px] text-slate-300 leading-relaxed">
                        Pick something you don&rsquo;t use anywhere else. Passwords are stored as bcrypt
                        hashes — even we can&rsquo;t read them.
                    </div>
                </div>
            </div>

            <div className="lg:col-span-2 flex items-center justify-center p-6 sm:p-10 bg-stone-50">
                <div className="w-full max-w-md bg-white rounded-2xl border border-stone-200/70 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.25)] p-8 sm:p-10 login-card" data-testid="reset-form">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="login-brandmark">
                            <ShieldCheck size={22} strokeWidth={2} />
                        </div>
                        <div className="leading-tight">
                            <div className="text-[15px] font-bold text-slate-900 tracking-[0.14em] leading-none">
                                CREDIT CARD
                            </div>
                            <div className="text-[13px] font-semibold text-slate-500 tracking-[0.14em] leading-none mt-1.5">
                                CONTROL CENTER
                            </div>
                        </div>
                    </div>

                    {checking ? (
                        <div className="flex items-center gap-2 text-slate-500 text-sm py-8" data-testid="reset-checking">
                            <Loader2 size={16} className="animate-spin" /> Checking your reset link…
                        </div>
                    ) : !tokenValid ? (
                        <div className="rounded-xl border border-red-200 bg-red-50 p-5" data-testid="reset-token-invalid">
                            <div className="flex items-start gap-3">
                                <AlertTriangle size={20} className="text-red-600 mt-0.5 shrink-0" />
                                <div>
                                    <div className="text-red-900 font-semibold">This link isn&rsquo;t usable.</div>
                                    <div className="text-red-800 text-sm mt-1">{tokenError}</div>
                                </div>
                            </div>
                            <Link
                                to="/forgot-password"
                                className="mt-4 inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium"
                                data-testid="reset-request-new-link"
                            >
                                Request a new link
                            </Link>
                        </div>
                    ) : done ? (
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5" data-testid="reset-success">
                            <div className="flex items-start gap-3">
                                <CheckCircle2 size={22} className="text-emerald-600 mt-0.5 shrink-0" />
                                <div>
                                    <div className="text-emerald-900 font-semibold">Password updated.</div>
                                    <div className="text-emerald-800 text-sm mt-1">
                                        Redirecting you to sign in…
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="mb-5">
                                <h2 className="text-[26px] font-semibold text-slate-900 leading-tight">
                                    Set a new password
                                </h2>
                                <p className="text-sm text-slate-500 mt-1">
                                    for <span className="font-medium text-slate-700">{tokenEmail}</span>
                                </p>
                            </div>

                            <form onSubmit={onSubmit}>
                                <label className="block mb-3">
                                    <span className="block text-[13px] font-medium text-slate-700 mb-1.5">
                                        New password
                                    </span>
                                    <span className="login-field">
                                        <KeyRound size={16} className="login-field__icon" strokeWidth={1.9} />
                                        <input
                                            type={showPw ? "text" : "password"}
                                            data-testid="reset-pw-input"
                                            value={pw}
                                            onChange={(e) => setPw(e.target.value)}
                                            placeholder="At least 8 characters"
                                            className="login-field__input"
                                            autoComplete="new-password"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPw((v) => !v)}
                                            className="login-field__eye"
                                            data-testid="reset-toggle-password"
                                            aria-label={showPw ? "Hide password" : "Show password"}
                                            tabIndex={-1}
                                        >
                                            {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </span>
                                </label>

                                <label className="block mb-4">
                                    <span className="block text-[13px] font-medium text-slate-700 mb-1.5">
                                        Confirm password
                                    </span>
                                    <span className="login-field">
                                        <KeyRound size={16} className="login-field__icon" strokeWidth={1.9} />
                                        <input
                                            type={showPw ? "text" : "password"}
                                            data-testid="reset-pw2-input"
                                            value={pw2}
                                            onChange={(e) => setPw2(e.target.value)}
                                            placeholder="Re-enter to confirm"
                                            className="login-field__input"
                                            autoComplete="new-password"
                                            required
                                        />
                                    </span>
                                    {pw2 && !matches && (
                                        <div className="text-xs text-red-600 mt-1.5" data-testid="reset-mismatch">
                                            Passwords do not match.
                                        </div>
                                    )}
                                </label>

                                <div className="mb-5 grid grid-cols-1 sm:grid-cols-3 gap-1.5" data-testid="reset-rules">
                                    {rulesState.map((r) => (
                                        <div
                                            key={r.label}
                                            className={`text-[11px] px-2 py-1 rounded-md border flex items-center gap-1 ${
                                                r.pass
                                                    ? "text-emerald-700 border-emerald-200 bg-emerald-50"
                                                    : "text-stone-500 border-stone-200 bg-stone-50"
                                            }`}
                                        >
                                            <CheckCircle2 size={11} strokeWidth={r.pass ? 2.4 : 1.8} />
                                            {r.label}
                                        </div>
                                    ))}
                                </div>

                                {error && (
                                    <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="reset-error">
                                        {error}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={busy || !strongEnough || !matches}
                                    className="login-signin-btn"
                                    data-testid="reset-submit-btn"
                                >
                                    <Lock size={16} strokeWidth={2.1} />
                                    {busy ? "Updating…" : "Update password"}
                                </button>
                            </form>
                        </>
                    )}

                    <div className="mt-6 pt-5 border-t border-slate-100">
                        <Link
                            to="/login"
                            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                            data-testid="reset-back-link"
                        >
                            Back to sign in
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
